<template>
  <div class="h-dvh flex flex-col bg-pitch-black text-total-white">
    <!-- Header Section -->
    <div
      class="flex w-full justify-between items-center px-4 p-3 border border-x-0 border-t-0 border-b-1 border-prime-golden bg-pitch-black-900"
    >
      <a href="/" class="text-prime-golden text-base font-avenir-medium">
        <img
          class="w-24 h-24 -my-8 -ml-[10px]"
          src="@/assets/images/mason-logo.svg"
          alt="Mason Logo"
        />
      </a>

      <!-- User Drawer Trigger -->
      <button @click="toggleDrawer" class="text-prime-golden text-xs w-4 h-4">
        <img src="@/assets/images/menu.svg" alt="Menu" />
      </button>
    </div>
    <ProjectHeader
      :projectName="projectName"
      :is_simulation="project?.is_simulation"
      :projectId="projectId"
      @openDeleteModal="openDeleteModal"
      :from="from"
      :logsCount="logsCount"
      :currentTab="currentTab"
      :role="user?.account_type"
      @setTab="setTab"
    />
    <div class="flex-1 flex flex-col overflow-y-auto">
      <!-- Check if loading -->
      <div v-if="loading" class="flex-1 flex items-center justify-center">
        <p>Loading project details...</p>
      </div>

      <!-- Project Content when not loading -->
      <div v-else>
        <!-- Project Header with Tab Switcher -->

        <!-- Dynamic Content Section -->
        <div class="overflow-y-auto flex-1 bg-pitch-black">
          <component :is="currentComponent" />
        </div>
      </div>

      <!-- Project Page Drawer -->
      <ProjectPageDrawer
        :isOpen="isDrawerOpen"
        :userPicture="user?.profile_image_url"
        :projectId="projectId"
        :isSimulation="project?.is_simulation"
        :projectName="projectName"
        :username="user?.username"
        :first_name="user?.first_name"
        :last_name="user?.last_name"
        :email="user?.email"
        :role="user?.account_type"
        :currentTab="currentTab"
        @openDeleteModal="openDeleteModal"
        @openResetModal="openResetModal"
        @close="toggleDrawer"
        @logout="handleLogout"
      />

      <!-- Delete Modal -->
      <DeleteModal
        v-if="isDeleteModalOpen"
        :isOpen="isDeleteModalOpen"
        @confirmDelete="deleteProject"
        @cancelDelete="closeDeleteModal"
      />

      <!-- Reset Modal -->
      <ResetModal
        v-if="isResetModalOpen"
        :isOpen="isResetModalOpen"
        @confirmReset="resetProject"
        @cancelReset="closeResetModal"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import ProjectHeader from "@/components/ProjectHeader.vue";
import OverviewView from "@/components/OverviewView.vue";
import LogView from "@/components/LogView.vue";
import FilesView from "@/components/FilesView.vue";
import ContactsView from "@/components/ContactsView.vue";
import ProjectPageDrawer from "@/components/ProjectPageDrawer.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { useUserStore } from "@/stores/user";
import { useProjectStore } from "@/stores/project";
import { useAuthStore } from "@/stores/auth";
import ResetModal from "@/components/ResetModal.vue";
import { useLogStore } from "@/stores/log";
import { useToast } from "vue-toastification";
import { useActivationStore } from "@/stores/activation";
import { useContactsStore } from "@/stores/contact";

export default {
  name: "ProjectPage",
  components: {
    ProjectHeader,
    ProjectPageDrawer,
    ResetModal,
    DeleteModal,
  },
  setup() {
    const isDrawerOpen = ref(false);
    const isResetModalOpen = ref(false);
    const isDeleteModalOpen = ref(false);
    const loading = ref(true); // New loading state
    const userStore = useUserStore();
    const projectStore = useProjectStore();
    const router = useRouter();
    const authStore = useAuthStore();
    const logStore = useLogStore();
    const logsCount = ref();
    const activationStore = useActivationStore();
    const contactsStore = useContactsStore();

    const toast = useToast();

    const projectName = computed(() => projectStore?.currentProject?.address_1 || "...");
    const projectId = computed(
      () => router.currentRoute.value.params.id || projectStore.currentProject?.id || null
    );
    const project = computed(() => projectStore.currentProject);
    const user = computed(() => userStore.profile);

    const currentTab = ref("overview");

    const from = ref("");

    const currentComponent = computed(() => {
      switch (currentTab.value) {
        case "overview":
          return OverviewView;
        case "log":
          return LogView;
        case "files":
          return FilesView;
        case "contacts":
          return ContactsView;
        default:
          return OverviewView; // Fallback to prevent null
      }
    });

    const setTab = (tab) => {
      currentTab.value = tab;
    };

    const toggleDrawer = () => {
      isDrawerOpen.value = !isDrawerOpen.value;
    };

    const resetProject = async () => {
      try {
        // Fetch the activation details
        const activationDetails = await activationStore.getActivation(projectId.value);

        if (!activationDetails) {
          toast.error("Failed to fetch project activation details.");
          return;
        }

        // Reset the project using the fetched activation details
        await activationStore.resetActivation({
          project_id: activationDetails.project?.id,
          agent_id: activationDetails.activation?.agent_stakeholder?.id,
          is_active: activationDetails.activation?.is_active,
          instruction_id: activationDetails.activation?.instruction?.id,
          agent_email_id: activationDetails.activation?.agent_email_id,
          agent_phone: activationDetails.activation?.agent_phone,
        });

        toast.success("Project reset successfully.");
        closeResetModal();
      } catch (error) {
        toast.error("Failed to reset project. Please try again.");
        closeResetModal();
        console.error("Error resetting project:", error);
      }
    };

    const openDeleteModal = () => {
      isDeleteModalOpen.value = true;
    };

    const openResetModal = () => {
      isResetModalOpen.value = true;
      isDrawerOpen.value = !isDrawerOpen.value;
    };

    const closeDeleteModal = () => {
      isDeleteModalOpen.value = false;
    };

    const closeResetModal = () => {
      isResetModalOpen.value = false;
    };

    const deleteProject = () => {
      console.log("Project deleted");
      projectStore.deleteProject(projectStore.currentProject.id);
      router.push({ name: "ProjectsPage" });
      closeDeleteModal();
    };

    const handleLogout = () => {
      authStore.logout();
      router.push({ name: "LogIn" });
    };

    // Fetch project details when the page is mounted
    onMounted(async () => {
      const { tab } = router.currentRoute.value.query;

      from.value = router.currentRoute.value.query.from;

      // Fetch the project and set the correct tab if it's available in query params
      if (tab) {
        currentTab.value = tab;
      } else {
        currentTab.value = "overview"; // Default to overview if no tab is specified
      }

      // Fetch user profile if not available
      if (!userStore.profile) {
        try {
          await userStore.fetchUserProfile();
        } catch (error) {
          console.error("Failed to fetch user profile:", error);
        }
      }

      // Extract the project ID from the URL
      const projectId = router.currentRoute.value.params.id;

      await logStore.fetchNewLogsByProjectId(projectId);
      await logStore.fetchThreadsByProjectId(projectId, "");

      const hasSystemLogs = logStore.threads.some((t) => t.comm_type === "agent_system");

      //const systemLogReduction = (hasSystemLogs && !showSystemLogs.value) ? 1 : 0;
      const systemLogReduction = 0;

      logsCount.value = Math.max(
        0,
        logStore.updatedThreadIds.length - systemLogReduction
      );
      await contactsStore.fetchAllContacts();
      // Fetch project if not already loaded
      if (!projectStore.currentProject || projectStore.currentProject.id !== projectId) {
        try {
          if (projectId) {
            await projectStore.fetchProjectById(projectId);
            console.log("Project fetched:", projectStore.currentProject);
          } else {
            console.error("No project ID found in route parameters.");
          }
        } catch (error) {
          console.error("Failed to fetch project:", error);
        }
      }

      loading.value = false; // Set loading to false once the project is fetched
    });

    return {
      isDrawerOpen,
      isDeleteModalOpen,
      from,
      user: user,
      projectName,
      projectId,
      currentTab,
      currentComponent,
      isResetModalOpen,
      setTab,
      resetProject,
      toggleDrawer,
      openDeleteModal,
      closeResetModal,
      closeDeleteModal,
      handleLogout,
      openResetModal,
      deleteProject,
      logsCount,
      loading, // Return the loading state
      project,
    };
  },
};
</script>

<style scoped>
/* Add any necessary styles */
</style>
